import { Component, OnInit, ChangeDetectionStrategy, Input, AfterViewInit, Output, EventEmitter, QueryList, ViewChildren, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WorkItemModel } from '../../models/work-item';
import { WorkItemService, TaxonomyFieldDefinitionService, WorkRelatedItemModel } from 'e5core-angular-services';
import { Guid } from 'guid-typescript';
import { E5coreUiWorkRelatedItemsBaseComponent } from './component/e5core-ui-work-related-items-base';
import { ViewerDisplayEvent } from '../../events/viewer-display-event';
import { OpenWorkEvent } from '../../events/open-work-event';
import { PrimeTreeTableModel, TreeTableDataModel } from '../../models/prime-tree-table.model';
import { CategorizationChangeEvent } from '../../events/categorization-change-event';
import { KeyValuePair } from '../../util/key-value-pair';
import { Dynamic } from '../../util/dynamic';

@Component({
  selector: 'e5core-ui-work-related-items',
  template: `
  <span *ngIf="workItem$">
    <e5core-ui-work-related-items-prime [labels]="labels" [showCreateChildButton]="showCreateChildButton" [createButtonText]="createButtonText" [showDefinedChildrenOnly]="showDefinedChildrenOnly" [readonly]="readonly" (workItemSelectEvent)="onWorkItemSelect($event)" (attachmentSelectEvent)="onAttachmentSelect($event)" (categorisationChangeEvent)="onCategorizationChange($event)" (createChildWorkItemEvent)="onCreateChildWorkItem()" (pageEvent)="onPageEvent($event)"></e5core-ui-work-related-items-prime>
  </span>
  <div *ngIf="!(workItem$)" class="e5core-component-error">&lt;e5core-ui-work-related-items&gt; [workItem$] must have a BehaviorSubject&lt;WorkItemModel&gt;</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class E5coreUiWorkRelatedItemsComponent  implements AfterViewInit, OnInit {

  @Input() workItem$ : BehaviorSubject<WorkItemModel>;
  @Input() findId : number = 0;
  @Input() createButtonText : string = "Create";
  @Input() fieldsToShow : KeyValuePair[] = null;
  @Input() readonly : boolean;
  @Input() pagesizes : number[];
  @Input() showCreateChildButton : boolean = true;
  @Input() showDefinedChildrenOnly : boolean = true;
  
  @Output() workItemSelectEvent = new EventEmitter<OpenWorkEvent>();
  @Output() attachmentSelectEvent = new EventEmitter<ViewerDisplayEvent>();
  @Output() categorisationChangeEvent = new EventEmitter<CategorizationChangeEvent>();
  @Output() createChildWorkItemEvent = new EventEmitter<OpenWorkEvent>();

  workId : string ;
  selectedPage: any;
  selectedCategory : any;
  labels:string[] = [] ;
  
  @ViewChildren(E5coreUiWorkRelatedItemsBaseComponent)
  public workRelatedItemsComponents: QueryList<E5coreUiWorkRelatedItemsBaseComponent>;

  constructor(
    private workItemService: WorkItemService,
    private fieldDefinitonServices: TaxonomyFieldDefinitionService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    if (typeof this.workItem$ == 'undefined') {
      console.error('e5core-ui-work-related-items [workItem$] must have a BehaviorSubject<WorkItemModel>');
      return ;
    }

    if(this.fieldsToShow) {
      this.fieldsToShow.forEach(element => {
        this.setLabel(element);
      });  
    }

    if (typeof this.workItem$ != 'undefined') {
      this.workItem$.subscribe(
        wim => {
          if (wim.workId != null && wim.workId != Guid.EMPTY) {
            this.workId = wim.workId;

            this.populateWorkRelatedItems(this.workId);

            if (this.showDefinedChildrenOnly)
              this.populateWorkClassChildren(wim.workId.toString());
          }
        }
      )
    }
  }

  private setLabel(findField : KeyValuePair) {
    if (findField.value == "") {
      var index = this.labels.length;
      this.labels[this.labels.length] = "";
      this.fieldDefinitonServices.get(findField.key).subscribe( 
        fd => {
          this.labels[index] = fd.label;
          this.changeDetectorRef.detectChanges();
        }) ;
    } else {
      this.labels[this.labels.length] = findField.value;
    }
  }

  private populateWorkClassChildren(wid: string) {
    this.workItemService.getWorkWorkClassChildren(wid).subscribe(
      s => {
        this.workRelatedItemsComponents.forEach(element => {
          element.childWorkItemCategories = s;
        });
      },
      error => {
        console.error(error);
      }
    )
  }

  private populateWorkRelatedItems(wid: string) {
    this.workItemService.getWorkRelatedItems(wid, {
      findId: this.findId,
      pageSize: this.selectedPage.pageSize,
      pageNo: this.selectedPage.pageIndex,
      doSearchFromRoot: true
      }).subscribe(
      s => {
        var rootModel = this.convertToUiModel(s.relatedItems);
        this.workRelatedItemsComponents.forEach(element => {
          element.length = s.total;
          element.treeData = <PrimeTreeTableModel[]>[rootModel];
          this.changeDetectorRef.detectChanges();
        });
        
        this.changeDetectorRef.detectChanges();
      },
      error => {
        console.error(error);
      }
    )
  }

  private convertToUiModel(apiModels: WorkRelatedItemModel[]): PrimeTreeTableModel {
    if(!apiModels)
      return ; // Nothing to do
      
    var current = apiModels.filter(u => u.id == this.workId);

    if (current.length == 0)
      return;

       
    var parent = apiModels.filter(u => u.id == current[0].parentId);
    
    if(current[0].parentId==Guid.EMPTY)
      parent = current;

    var children = apiModels.filter(u => u.parentId == this.workId);

    var rootModel = new PrimeTreeTableModel();
    rootModel.data.id = parent[0].id;
    rootModel.data.attachmentCount = parent[0].attachmentCount;
    rootModel.data.parentId = parent[0].parentId;
    rootModel.expanded = true;
    this.populateDataFields(rootModel.data, parent[0]);
    this.processChildren(rootModel, children, apiModels)

    return rootModel;
  }

  private processChildren(parent: PrimeTreeTableModel, children: WorkRelatedItemModel[], apiModels: WorkRelatedItemModel[]) {

    for (let i = 0; i < children.length; i++) {
      var child = children[i];
      var model = new PrimeTreeTableModel();
      model.data.id = child.id;
      model.data.attachmentCount = child.attachmentCount;
      model.data.parentId = child.parentId;

      this.populateDataFields(model.data, child);
      parent.children[parent.children.length] = model;

      var moreChildren = apiModels.filter(u => u.parentId == child.id);

      if (moreChildren.length > 0) {
        this.processChildren(model, moreChildren, apiModels);
      }
    }
  }

  private populateDataFields(modelData: TreeTableDataModel, child: any) {
    if(!this.fieldsToShow) {
      modelData.fieldValues = [] ;
    } else {
      for(var i = 0; i < this.fieldsToShow.length; i++) {
        var findField = this.fieldsToShow[i];
        var field = child.fieldValues.filter(u => u.key == findField.key);
        if (field == null || field.length == 0) {
          field = [{key: findField.key, value: ""}]
        }
  
        var keyValuePair = new KeyValuePair();
        keyValuePair.key = field[0].key;
        keyValuePair.value = field[0].value;
        modelData.fieldValues[modelData.fieldValues.length] = keyValuePair;
      }  
    }
  }
  
  ngAfterViewInit() {
  }

  public CreateChildWorkItem(selectedCategory : any) : void {
    if(!this.workId) {
      console.error("e5core-ui-work-related-items has no work item.") ;
      return ;
    }

    var workItemCreateModel = {
          category1Id: selectedCategory.category1Id,
          category2Id: selectedCategory.category2Id,
          category3Id: selectedCategory.category3Id,
          workItemFields: [
            {
              fieldId: "Parent_id",
              fieldValue: this.workId,
              fieldType: "Guid"
            }
          ] 
        };

    this.workItemService.create(workItemCreateModel).subscribe(
      s => {
        this.createChildWorkItemEvent.emit({workId: s.id, reference: s.reference, type: "related"});
        this.populateWorkRelatedItems(this.workId);
      },
      error => {
        console.error(error);
      }
    )
  }

  public onCreateChildWorkItem() : void {
    this.CreateChildWorkItem(this.selectedCategory);
  }

  public onCategorizationChange($event: CategorizationChangeEvent) : void {
    this.selectedCategory = $event;
    this.categorisationChangeEvent.emit($event);
  }

  public onWorkItemSelect(openWorkEvent : OpenWorkEvent) : void {
    this.workItemSelectEvent.emit(openWorkEvent);
  }

  public onAttachmentSelect(viewerDisplayEvent : ViewerDisplayEvent) : void {
    this.attachmentSelectEvent.emit(viewerDisplayEvent);
  }

  public onPageEvent(event) : void {
    this.selectedPage = event;
    
    if (typeof this.workId != 'undefined' && this.workId != Guid.EMPTY) {
      this.populateWorkRelatedItems(this.workId);
    }
  }
}
